import { Button, Flex, Form, Input, message, Modal, Select } from 'antd';

import styles from './index.module.scss';
import { CloseOutlined } from '@ant-design/icons';
import { ReportCreateBody } from '../../../store/report/create';
import { useReportCreate } from '../../../hooks/api/reports';
import { getMessageInError } from '../../../hooks/fetch';
import TextArea from 'antd/es/input/TextArea';

interface FormValues {
  reason: string;
  name: string;
  description: string;
}

type CreateBody =
  | {
      type: 'comment';
      commentId: string;
    }
  | {
      type: 'business';
      businessId: string;
    }
  | {
      type: 'post';
      postId: string;
    };

interface ModalReportProps {
  isOpen: boolean;
  onCancel: () => void;
  createBody: CreateBody;
}

export const ModalReport = ({
  isOpen,
  onCancel,
  createBody,
}: ModalReportProps) => {
  const createReport = useReportCreate();
  const [form] = Form.useForm<FormValues>();

  const onFinish = (values: FormValues) => {
    const body: ReportCreateBody = {
      type: createBody.type,
      reason: values.reason,
      name: (values.name || '').slice(0, 100), // safety clamp
      description: (values.description || '').slice(0, 2000), // safety clamp
    };

    if (createBody.type === 'comment') {
      body.commentId = createBody.commentId;
    } else if (createBody.type === 'business') {
      body.businessId = createBody.businessId;
    } else if (createBody.type === 'post') {
      body.postId = createBody.postId;
    }

    createReport
      .fetch(body)
      .then((data) => {
        if (data) {
          message
            .success('Report Submitted Successfully')
            .then(createReport.clearResponse);
          form.resetFields();
          onCancel();
        }
      })
      .catch((error) => {
        message.error(getMessageInError(error)).then(createReport.clearError);
      });
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      wrapClassName={styles.wrapper}
      className={styles.modal}
      closable={true}
      closeIcon={<CloseOutlined className={styles.cancelBtn} />}
    >
      <div className={styles.modalContent}>
        <Form<FormValues>
          form={form}
          autoComplete="off"
          layout="vertical"
          onFinish={onFinish}
          className={styles.form}
        >
          <Form.Item
            name="reason"
            rules={[{ required: true, message: 'Please select reason' }]}
            className={styles.inputFormItem}
          >
            <Select placeholder="Select Reason" size="large">
              <Select.Option value="spam">Spam</Select.Option>
              <Select.Option value="abuse">Abuse</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            rules={[
              { required: true, message: 'Please enter reason title' },
              { max: 100, message: 'Title must be at most 100 characters' },
            ]}
            className={styles.inputFormItem}
          >
            <Input
              size="large"
              placeholder="Enter Reason Title"
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="description"
            rules={[
              { required: true, message: 'Please enter description' },
              {
                max: 2000,
                message: 'Description must be at most 2000 characters',
              },
            ]}
            className={styles.inputFormItem}
          >
            <TextArea
              size="large"
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder="Enter description"
              maxLength={2000}
              showCount
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  form.submit();
                }
              }}
            />
          </Form.Item>

          <Flex justify="flex-end">
            <Button
              type="primary"
              className={styles.submit}
              onClick={form.submit}
              loading={createReport.loading}
              block
              size="large"
            >
              Send
            </Button>
          </Flex>
        </Form>
      </div>
    </Modal>
  );
};
